import type { CommentAnchor } from 'features/libra/comments/commentTypes'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useCallback } from 'react'

import type { ZoneTabKey } from './zoneAnalysisApi'
import type { ZoneAnalysisAnchor } from './zoneCommentAnchors'

type ZonePropertyAnchor = Extract<ZoneAnalysisAnchor, { type: 'zone-property' }>

/** Each zone-card property key, mapped to the card tab that renders it. */
const ZONE_PROPERTY_TABS = {
  // ZoneDescriptionPanel
  title: 'basic',
  description: 'basic',
  review_notes: 'basic',
  assumptions: 'basic',
  zone_recommendations: 'basic',
  // DesignFunctionPanel
  material: 'design',
  geometry: 'design',
  height_mm: 'design',
  external_diameter_mm: 'design',
  length_basis: 'design',
  cylinder_proportion: 'design',
  head_type: 'design',
  head_proportion: 'design',
  total_surface_area_sq_m: 'design',
  wall_thickness_shell_mm: 'design',
  wall_thickness_heads_mm: 'design',
  corrosion_allowance_mm: 'design',
  design_pressure_bar: 'design',
  operating_pressure_bar: 'design',
  design_temp_c: 'design',
  max_operating_temp_c: 'design',
  // NiiStrategyPanel
  'nii_strategy.degradation_likely': 'strategy',
  'nii_strategy.degradation_predictable': 'strategy',
  'nii_strategy.degradation_severity': 'strategy',
  'nii_strategy.strategy_type_override': 'strategy',
  // RbaInformationPanel
  'rba_info.density_of_degradation': 'rba',
  'rba_info.measurement_accuracy': 'rba',
  'rba_info.tolerance_of_degradation': 'rba',
  'rba_info.spatial_homogeneity': 'rba',
} as const satisfies Record<string, ZoneTabKey>

export type ZonePropertyKey = keyof typeof ZONE_PROPERTY_TABS

/**
 * The zone-card tab that renders an anchor, or undefined when the anchor is
 * not inside the card (e.g. a `zone` anchor sits on the zone row itself).
 */
export function getZoneCardTab(anchor: CommentAnchor): ZoneTabKey | undefined {
  if (anchor.type === 'damage-mechanism') return 'damage'
  if (anchor.type !== 'zone-property') return undefined
  return Object.hasOwn(ZONE_PROPERTY_TABS, anchor.property)
    ? ZONE_PROPERTY_TABS[anchor.property as ZonePropertyKey]
    : undefined
}

export function useZonePropertyAnchor(
  zoneId: string
): (property: ZonePropertyKey) => ZonePropertyAnchor {
  const stepId = useActiveStepId()
  return useCallback(
    (property: ZonePropertyKey): ZonePropertyAnchor => ({
      type: 'zone-property',
      stepId,
      zoneId,
      property,
    }),
    [stepId, zoneId]
  )
}
